import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Search, MoreHorizontal, Edit, Trash2, Filter, Upload, Eye } from 'lucide-react';
import { AddQuestionDialog } from './AddQuestionDialog';
import { EditQuestionDialog } from './EditQuestionDialog';
import { ViewQuestionDialog } from './ViewQuestionDialog';
import { QuestionTypeTest } from '../debug/QuestionTypeTest';
import { kitApi, questionApi } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

interface Question {
  id: string;
  questionText: string;
  type: string;
  options: string[];
  correctAnswer?: string; // For FILL_IN_THE_BLANK_NUMERIC questions
  correctAnswers: string[]; // For MCQ questions
  explanation?: string;
  subject: string;
  topic: string;
  subtopic?: string;
  difficultyLevel: string;
  examTypes?: string[]; // NEW: Multiple exam types support
  kitId?: string;
  tenantCode?: string;
  tenantId?: string | null;
  tenantCodes?: string[];
  accessScope?: 'GLOBAL' | 'TENANT';
  sourceType?: 'SUPER_ADMIN' | 'ADMIN';
  createdAt: string;
  updatedAt: string;
}

interface QuestionFilters {
  search: string;
  subject: string;
  topic: string;
  difficulty: string;
  questionType: string;
}

export const QuestionManagement: React.FC = () => {
  const { toast } = useToast();

  // State
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

  // Filters
  const [filters, setFilters] = useState<QuestionFilters>({
    search: '',
    subject: 'all',
    topic: 'all',
    difficulty: 'all',
    questionType: 'all'
  });

  // Filter options
  const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
  const [topicOptions, setTopicOptions] = useState<string[]>([]);
  const [kitNameOptions, setKitNameOptions] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedKitName, setSelectedKitName] = useState('');
  const [uploading, setUploading] = useState(false);

  // Load questions on component mount and when filters change
  useEffect(() => {
    loadQuestions();
  }, [currentPage, filters]);

  // Load filter options
  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: 20,
        sortBy: 'updatedAt',
        sortDir: 'desc',
        ...(filters.search && { search: filters.search }),
        ...(filters.subject && filters.subject !== 'all' && { subject: filters.subject }),
        ...(filters.topic && filters.topic !== 'all' && { topic: filters.topic }),
        ...(filters.difficulty && filters.difficulty !== 'all' && { difficulty: filters.difficulty }),
        ...(filters.questionType && filters.questionType !== 'all' && { questionType: filters.questionType })
      };

      const response = await questionApi.getAll(params);

      // Handle paginated response
      if (response.content) {
        setQuestions(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } else {
        // Handle non-paginated response
        setQuestions(response);
        setTotalElements(response.length);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      toast({
        title: "Error",
        description: "Failed to load questions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [subjects, topics, kitNames] = await Promise.all([
        questionApi.searchSubjects(''),
        questionApi.searchTopics('', ''),
        kitApi.getKitNames()
      ]);

      setSubjectOptions(subjects);
      setTopicOptions(topics);
      setKitNameOptions(kitNames);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'File required',
        description: 'Select a CSV or Excel file to upload questions.',
        variant: 'destructive'
      });
      return;
    }

    if (!selectedKitName.trim()) {
      toast({
        title: 'Kit name required',
        description: 'Provide a kit name. Uploaded questions will use this as the primary selection criteria for test creation.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploading(true);
      const result = await questionApi.bulkUpload(selectedFile, {
        kitName: selectedKitName.trim()
      });

      toast({
        title: 'Upload completed',
        description: result?.message || 'Questions uploaded successfully.'
      });

      setSelectedFile(null);
      setSelectedKitName('');
      loadQuestions();
      loadFilterOptions();
    } catch (error: any) {
      console.error('Error uploading questions:', error);
      toast({
        title: 'Upload failed',
        description: error?.response?.data?.message || error?.response?.data?.error || 'Failed to upload questions',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof QuestionFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      subject: 'all',
      topic: 'all',
      difficulty: 'all',
      questionType: 'all'
    });
    setCurrentPage(0);
  };

  // CRUD operations
  const handleAddQuestion = () => {
    setShowAddDialog(true);
  };

  const handleEditQuestion = (question: Question) => {
    if (!isEditableByAdmin(question)) {
      toast({
        title: 'Read-only question',
        description: 'Shared super-admin questions can be viewed here but must be edited from the super-admin portal.'
      });
      return;
    }
    setSelectedQuestion(question);
    setShowEditDialog(true);
  };

  const handleViewQuestion = (question: Question) => {
    setSelectedQuestion(question);
    setShowViewDialog(true);
  };

  const handleDeleteQuestion = (question: Question) => {
    if (!isEditableByAdmin(question)) {
      toast({
        title: 'Read-only question',
        description: 'Shared super-admin questions cannot be deleted from the admin portal.',
        variant: 'destructive'
      });
      return;
    }
    setSelectedQuestion(question);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedQuestion) return;

    try {
      await questionApi.delete(selectedQuestion.id);
      toast({
        title: "Success",
        description: "Question deleted successfully"
      });
      loadQuestions();
    } catch (error: any) {
      console.error('Error deleting question:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete question",
        variant: "destructive"
      });
    } finally {
      setShowDeleteDialog(false);
      setSelectedQuestion(null);
    }
  };

  // Utility functions
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'EASY': return 'bg-green-100 text-green-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'HARD': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getQuestionTypeLabel = (questionType: string) => {
    switch (questionType) {
      case 'MCQ_SINGLE_CORRECT': return 'MCQ Single';
      case 'MCQ_MULTIPLE_CORRECT': return 'MCQ Multiple';
      case 'FILL_IN_THE_BLANK_NUMERIC': return 'Numeric';
      case 'TRUE_FALSE': return 'True/False';
      default: return questionType;
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getQuestionScope = (question: Question) => {
    if (question.accessScope) return question.accessScope;
    return question.tenantId ? 'TENANT' : 'GLOBAL';
  };

  const getSourceLabel = (question: Question) => {
    if (question.sourceType === 'SUPER_ADMIN' || getQuestionScope(question) === 'GLOBAL') {
      return 'Super Admin';
    }

    return 'Admin Tenant';
  };

  const getAccessLabel = (question: Question) => {
    if (getQuestionScope(question) === 'GLOBAL') {
      return 'All admin tenants';
    }

    if (question.tenantCodes && question.tenantCodes.length > 0) {
      return question.tenantCodes.join(', ');
    }

    if (question.tenantCode) {
      return question.tenantCode;
    }

    return 'Current tenant only';
  };

  const isEditableByAdmin = (question: Question) => getQuestionScope(question) === 'TENANT';

  return (
    <div className="space-y-6">
      {/* Debug Test Component - Remove after testing */}
      <QuestionTypeTest />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Question Management</h1>
          <p className="text-muted-foreground">
            Manage tenant questions and review super-admin questions shared across tenants
          </p>
        </div>
        <Button onClick={handleAddQuestion}>
          <Plus className="h-4 w-4 mr-2" />
          Add Question
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Bulk Upload by Kit Name
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_auto] gap-4 items-end">
            <div>
              <p className="text-sm font-medium mb-2">Question file</p>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Upload questions and bind them to a kit name so admins can use that kit directly during test creation.
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Kit name</p>
              <Input
                list="admin-question-kit-names"
                placeholder="Type or select a kit name"
                value={selectedKitName}
                onChange={(e) => setSelectedKitName(e.target.value)}
              />
              <datalist id="admin-question-kit-names">
                {kitNameOptions.map((kitName) => (
                  <option key={kitName} value={kitName} />
                ))}
              </datalist>
            </div>
            <Button onClick={handleBulkUpload} disabled={uploading || !selectedFile || !selectedKitName.trim()}>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Search */}
            <div className="xl:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search questions..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Subject */}
            <Select value={filters.subject} onValueChange={(value) => handleFilterChange('subject', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjectOptions.map(subject => (
                  <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Topic */}
            <Select value={filters.topic} onValueChange={(value) => handleFilterChange('topic', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Topics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Topics</SelectItem>
                {topicOptions.map(topic => (
                  <SelectItem key={topic} value={topic}>{topic}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Difficulty */}
            <Select value={filters.difficulty} onValueChange={(value) => handleFilterChange('difficulty', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="EASY">Easy</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HARD">Hard</SelectItem>
              </SelectContent>
            </Select>

            {/* Question Type */}
            <Select value={filters.questionType} onValueChange={(value) => handleFilterChange('questionType', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="MCQ_SINGLE_CORRECT">MCQ Single</SelectItem>
                <SelectItem value="MCQ_MULTIPLE_CORRECT">MCQ Multiple</SelectItem>
                <SelectItem value="FILL_IN_THE_BLANK_NUMERIC">Fill in the Blank</SelectItem>
                <SelectItem value="TRUE_FALSE">True/False</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          <div className="flex justify-between items-center mt-4">
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
            <p className="text-sm text-muted-foreground">
              {totalElements} question{totalElements !== 1 ? 's' : ''} found
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Questions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Topic</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Accessible To</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        <span className="ml-2">Loading questions...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : questions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No questions found
                    </TableCell>
                  </TableRow>
                ) : (
                  questions.map((question) => (
                    <TableRow key={question.id}>
                      <TableCell className="max-w-xs">
                        <div className="break-words">
                          {truncateText(question.questionText)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{question.subject}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{question.topic}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getQuestionTypeLabel(question.type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getDifficultyColor(question.difficultyLevel)}>
                          {question.difficultyLevel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getQuestionScope(question) === 'GLOBAL' ? (
                          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Global</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Tenant</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline">{getSourceLabel(question)}</Badge>
                          <p className="text-xs text-muted-foreground">{getAccessLabel(question)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewQuestion(question)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            {isEditableByAdmin(question) ? (
                              <>
                                <DropdownMenuItem onClick={() => handleEditQuestion(question)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteQuestion(question)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem disabled>
                                Shared super-admin question (read-only)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                Page {currentPage + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AddQuestionDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => {
          loadQuestions();
          setShowAddDialog(false);
        }}
      />

      <EditQuestionDialog
        isOpen={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedQuestion(null);
        }}
        onSuccess={() => {
          loadQuestions();
          setShowEditDialog(false);
          setSelectedQuestion(null);
        }}
        question={selectedQuestion}
      />

      <ViewQuestionDialog
        isOpen={showViewDialog}
        onClose={() => {
          setShowViewDialog(false);
          setSelectedQuestion(null);
        }}
        question={selectedQuestion}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedQuestion(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
